import { apiClient, handleApiError } from "./client";
import { MasterOnboardingStep } from "@chess-master/schemas";

export interface LichessPerf {
  rating: number;
  games?: number;
  rd?: number;
  prog?: number;
  prov?: boolean;
  rank?: number;
}

export type LichessRatings = Record<string, LichessPerf>;
export type ProfileSections = Array<{ title: string; content: string }>;

export interface UpdateUserData {
  email?: string;
  username?: string;
  name?: string | null;
  lastname?: string | null;
  title?: string | null;
  rating?: number | null;
  bio?: string | null;
  profileSections?: ProfileSections | null;
  isMaster?: boolean;
  isPrivate?: boolean;
  chesscomUrl?: string | null;
  lichessUrl?: string | null;
  lichessRatings?: LichessRatings | null;
  hourlyRate?: number | null;
  languages?: string[] | null;
  teachingFocuses?: string[] | null;
  youtubeVideos?: string[] | null;
  phoneNumber?: string | null;
  location?: string | null;
  country?: string | null;
  twitchUrl?: string | null;
  youtubeUrl?: string | null;
  instagramUrl?: string | null;
  xUrl?: string | null;
  facebookUrl?: string | null;
  tiktokUrl?: string | null;
  onboardingStatus?: MasterOnboardingStep;
}

export interface User {
  id: number;
  username: string;
  email: string;
  name?: string | null;
  lastname?: string | null;
  title?: string | null;
  rating?: number | null;
  bio?: string | null;
  profileSections?: ProfileSections | null;
  isMaster: boolean;
  isPrivate?: boolean;
  status?: "active" | "disabled";
  profilePictureUrl?: string | null;
  profilePictureThumbnailUrl?: string | null;
  chesscomUrl?: string | null;
  lichessUrl?: string | null;
  lichessRatings?: LichessRatings | null;
  hourlyRate?: number | null;
  languages?: string[] | null;
  teachingFocuses?: string[] | null;
  youtubeVideos?: string[] | null;
  location?: string | null;
  country?: string | null;
  twitchUrl?: string | null;
  youtubeUrl?: string | null;
  instagramUrl?: string | null;
  xUrl?: string | null;
  facebookUrl?: string | null;
  tiktokUrl?: string | null;
  onboardingStatus?: MasterOnboardingStep;
}

/**
 * Get current authenticated user
 */
export const getMe = async (): Promise<{ user: User }> => {
  try {
    const response = await apiClient.get("/users/me");
    return response.data;
  } catch (error: any) {
    throw new Error(handleApiError(error));
  }
};

/**
 * Update user profile
 */
export const updateUser = async (
  id: number,
  data: UpdateUserData
): Promise<{ status: string; user: User }> => {
  try {
    const response = await apiClient.patch(`/users/${id}`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(handleApiError(error));
  }
};

/**
 * Find users with filters
 */
export const findUsers = async (filters: {
  isMaster?: boolean;
  username?: string;
  minRating?: number;
  maxRating?: number;
  title?: string;
  limit?: number;
}): Promise<{ status: string; users: User[] }> => {
  try {
    const response = await apiClient.get("/users", { params: filters });
    return response.data;
  } catch (error: any) {
    throw new Error(handleApiError(error));
  }
};

export const getPublicUser = async (userId: number) => {
  const res = await apiClient.get(`/users/${userId}`);
  if (!res.data.user) throw new Error("User not found");
  return res.data.user;
};

export const getPublicUserByUsername = async (username: string) => {
  const res = await apiClient.get(`/users/username/${encodeURIComponent(username)}`);
  if (!res.data.user) throw new Error("User not found");
  return res.data.user;
};

export const uploadProfilePicture = async (file: File) => {
  const formData = new FormData();
  formData.append("image", file);

  const res = await apiClient.post(`/users/images`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  if (!res.data.success) throw new Error("Upload failed");
  return res.data;
};
